"use client"

import React, { useState, useEffect } from "react"
import { useSearchParams } from "next/navigation"
import { Search, Filter, X } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { Post } from "@/lib/posts"
import PostCard from "@/components/post-card"
import TagCloud from "@/components/tag-cloud"

export default function BlogPage() {
  // In client components, we can use useSearchParams directly without React.use
  const searchParams = useSearchParams()
  const initialSearchQuery = searchParams.get("search") || ""
  const initialCategory = searchParams.get("category") || "All"
  const initialTag = searchParams.get("tag") || ""

  const [posts, setPosts] = useState<Post[]>([])
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([])
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery)
  const [activeCategory, setActiveCategory] = useState(initialCategory)
  const [activeTag, setActiveTag] = useState(initialTag)
  const [isLoading, setIsLoading] = useState(true)
  const [isFiltersVisible, setIsFiltersVisible] = useState(false)
  const [availableTags, setAvailableTags] = useState<{ name: string; count: number }[]>([])

  // Categories for the blog
  const categories = ["All", "Adventure", "Food", "Culture", "Tips", "Budget", "Wildlife", "Beaches"]

  // Fetch posts on component mount
  useEffect(() => {
    async function fetchPosts() {
      try {
        setIsLoading(true)
        const response = await fetch("/api/posts")
        if (!response.ok) {
          throw new Error("Failed to fetch posts")
        }
        const data = await response.json()
        setPosts(data)

        // Extract tags from posts
        const tagCounts: Record<string, number> = {}
        data.forEach((post: Post) => {
          post.tags.forEach((tag: string) => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1
          })
        })
        const sortedTags = Object.entries(tagCounts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
        setAvailableTags(sortedTags)

        // Apply initial filters from URL
        let filtered = data
        if (initialSearchQuery) {
          filtered = filterBySearch(filtered, initialSearchQuery)
        }
        if (initialCategory !== "All") {
          filtered = filterByCategory(filtered, initialCategory)
        }
        if (initialTag) {
          filtered = filterByTag(filtered, initialTag)
        }

        setFilteredPosts(filtered)
        setIsLoading(false)
      } catch (error) {
        console.error("Error fetching posts:", error)
        setIsLoading(false)
      }
    }

    fetchPosts()
  }, [initialSearchQuery, initialCategory, initialTag])

  // Filter by search query
  const filterBySearch = (postsToFilter: Post[], query: string) => {
    if (!query) return postsToFilter

    const lowercaseQuery = query.toLowerCase()
    return postsToFilter.filter(
      (post) =>
        post.title.toLowerCase().includes(lowercaseQuery) ||
        post.excerpt.toLowerCase().includes(lowercaseQuery) ||
        post.location.toLowerCase().includes(lowercaseQuery) ||
        post.tags.some((tag) => tag.toLowerCase().includes(lowercaseQuery)) ||
        post.categories.some((category) => category.toLowerCase().includes(lowercaseQuery)),
    )
  }

  // Filter by category
  const filterByCategory = (postsToFilter: Post[], category: string) => {
    if (category === "All") return postsToFilter

    return postsToFilter.filter((post) => post.categories.some((cat) => cat.toLowerCase() === category.toLowerCase()))
  }

  // Filter by tag
  const filterByTag = (postsToFilter: Post[], tag: string) => {
    if (!tag) return postsToFilter

    return postsToFilter.filter((post) => post.tags.some((postTag) => postTag.toLowerCase() === tag.toLowerCase()))
  }

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)
  }

  // Handle search form submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters(searchQuery, activeCategory, activeTag)
  }

  // Apply all filters and update URL
  const applyFilters = (search: string, category: string, tag: string) => {
    let filtered = posts

    if (search) {
      filtered = filterBySearch(filtered, search)
    }
    if (category !== "All") {
      filtered = filterByCategory(filtered, category)
    }
    if (tag) {
      filtered = filterByTag(filtered, tag)
    }

    setFilteredPosts(filtered)

    // Update URL with parameters
    const url = new URL(window.location.href)
    if (search) {
      url.searchParams.set("search", search)
    } else {
      url.searchParams.delete("search")
    }

    if (category !== "All") {
      url.searchParams.set("category", category)
    } else {
      url.searchParams.delete("category")
    }

    if (tag) {
      url.searchParams.set("tag", tag)
    } else {
      url.searchParams.delete("tag")
    }

    window.history.pushState({}, "", url.toString())
  }

  // Handle category selection
  const handleCategoryClick = (category: string) => {
    setActiveCategory(category)
    applyFilters(searchQuery, category, activeTag)
  }

  // Handle tag selection
  const handleTagClick = (tag: string) => {
    if (activeTag === tag) {
      // Deselect if same tag clicked
      setActiveTag("")
      applyFilters(searchQuery, activeCategory, "")
    } else {
      setActiveTag(tag)
      applyFilters(searchQuery, activeCategory, tag)
    }
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("")
    setActiveCategory("All")
    setActiveTag("")
    setFilteredPosts(posts)

    // Remove search parameters from URL
    const url = new URL(window.location.href)
    url.search = ""
    window.history.pushState({}, "", url.toString())
  }

  // Toggle filters visibility on mobile
  const toggleFilters = () => {
    setIsFiltersVisible(!isFiltersVisible)
  }

  return (
    <main className="min-h-screen py-12">
      <div className="container px-4 sm:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Travel Blog</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6 md:mb-8">
            Explore our collection of travel stories, tips, and guides from around the world
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Input
                type="search"
                placeholder="Search blog posts..."
                className="pl-10 py-6"
                value={searchQuery}
                onChange={handleSearchChange}
                aria-label="Search blog posts"
              />
              <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <Button type="submit" size="sm" variant="ghost" className="absolute right-2 top-2" aria-label="Search">
                <Search className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="md:hidden mb-6 flex justify-between items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFilters}
            className="flex items-center gap-2"
            aria-expanded={isFiltersVisible}
            aria-controls="mobile-filters"
          >
            <Filter className="h-4 w-4" />
            Filters
            {(searchQuery || activeCategory !== "All" || activeTag) && (
              <span className="bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center ml-1">
                {(searchQuery ? 1 : 0) + (activeCategory !== "All" ? 1 : 0) + (activeTag ? 1 : 0)}
              </span>
            )}
          </Button>

          {(searchQuery || activeCategory !== "All" || activeTag) && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" />
              Clear filters
            </Button>
          )}
        </div>

        {/* Mobile Filters */}
        {isFiltersVisible && (
          <div id="mobile-filters" className="md:hidden bg-accent rounded-lg p-4 mb-6">
            <h2 className="font-medium mb-3">Categories</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={category === activeCategory ? "default" : "outline"}
                  size="sm"
                  className="rounded-full"
                  onClick={() => handleCategoryClick(category)}
                >
                  {category}
                </Button>
              ))}
            </div>

            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={toggleFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Desktop Categories */}
        <div className="hidden md:flex flex-wrap justify-center gap-2 mb-8 md:mb-12">
          {categories.map((category) => (
            <Button
              key={category}
              variant={category === activeCategory ? "default" : "outline"}
              className="rounded-full"
              onClick={() => handleCategoryClick(category)}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Tag Cloud */}
        {!isLoading && availableTags.length > 0 && (
          <div className="mb-8">
            <TagCloud
              tags={availableTags}
              selectedTag={activeTag}
              onTagClick={handleTagClick}
              className="max-w-2xl mx-auto"
            />
          </div>
        )}

        {/* Active Filters Display */}
        {(searchQuery || activeCategory !== "All" || activeTag) && (
          <div className="flex flex-wrap items-center gap-2 mb-6 text-sm">
            <span className="text-muted-foreground">Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center rounded-full bg-secondary/10 text-secondary text-xs px-2 py-0.5 font-medium">
                Search: {searchQuery}
              </span>
            )}
            {activeCategory !== "All" && (
              <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5 font-medium">
                Category: {activeCategory}
              </span>
            )}
            {activeTag && (
              <span className="inline-flex items-center rounded-full bg-accent text-accent-foreground text-xs px-2 py-0.5 font-medium">
                Tag: {activeTag}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground h-7 px-2">
              <X className="h-3 w-3 mr-1" />
              Clear All
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="rounded-xl overflow-hidden shadow-sm bg-card h-full">
                <Skeleton className="h-48 w-full" />
                <div className="p-5 md:p-6">
                  <div className="flex gap-4 mb-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-1" />
                  <Skeleton className="h-4 w-3/4 mb-4" />
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results Message */}
        {!isLoading && filteredPosts.length === 0 && (
          <div className="text-center py-16 md:py-20">
            <h2 className="text-xl md:text-2xl font-bold mb-2">No posts found</h2>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filter to find what you're looking for.
            </p>
            <Button onClick={clearFilters}>Clear Filters</Button>
          </div>
        )}

        {/* Blog Posts Grid */}
        {!isLoading && filteredPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredPosts.map((post) => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && filteredPosts.length > 0 && (
          <div className="flex justify-center mt-10 md:mt-12">
            <nav className="flex items-center gap-1" aria-label="Pagination">
              <Button variant="outline" size="icon" disabled className="h-10 w-10">
                <span className="sr-only">Previous page</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="m15 18-6-6 6-6"></path>
                </svg>
              </Button>
              <Button variant="outline" size="icon" className="bg-primary text-white hover:bg-primary/90 h-10 w-10">
                <span className="sr-only">Page 1</span>1
              </Button>
              <Button variant="outline" size="icon" className="h-10 w-10">
                <span className="sr-only">Page 2</span>2
              </Button>
              <Button variant="outline" size="icon" className="h-10 w-10">
                <span className="sr-only">Page 3</span>3
              </Button>
              <Button variant="outline" size="icon" className="h-10 w-10">
                <span className="sr-only">Next page</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="m9 18 6-6-6-6"></path>
                </svg>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </main>
  )
}
